import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Badge, Button, Card, Col, Row, Spinner } from 'react-bootstrap'
import DataTable from 'react-data-table-component'
import { FaArrowsRotate, FaCheck, FaFileSignature, FaKey } from 'react-icons/fa6'
import { useNavigate } from 'react-router-dom'
import RegistrasiService from '../../services/registrasi/RegistrasiService'
import signatureService from '../../services/signature/SignatureService'
import NotificationService from '../../services/notification/NotificationService'

const PAGE_SIZE = 10

const formatDate = value => value ?
    new Date(value).toLocaleString('id-ID', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    })
    : '-'

const isCertificateNotification = item =>
    item?.type === 'SIGNATURE_CERTIFICATE_REQUIRED' ||
    item?.action === 'OPEN_CERTIFICATE'

const isApprovalNotification = item =>
    item?.type === 'DOCUMENT_APPROVAL_REQUIRED' ||
    item?.action === 'OPEN_DOCUMENT_APPROVAL'

const isSignatureNotification = item =>
    item?.type === 'SIGNATURE_REQUIRED' ||
    item?.action === 'OPEN_SIGNATURE'

const isSignedDocumentNotification = item =>
    item?.type === 'SIGNATURE_SUCCESS' ||
    item?.type === 'DOCUMENT_SIGNED' ||
    item?.action === 'OPEN_SIGNED_DOCUMENT'

const isActionable = item =>
    isApprovalNotification(item) ||
    isCertificateNotification(item) ||
    isSignatureNotification(item) ||
    isSignedDocumentNotification(item)

export default function NotificationPage() {
    const navigate = useNavigate()

    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [size, setSize] = useState(PAGE_SIZE)
    const [totalRows, setTotalRows] = useState(0)

    // Service dibuat satu kali selama komponen hidup.
    const notificationService = useMemo(() => new NotificationService(), [])

    // Mencegah request identik berjalan bersamaan.
    const requestRef = useRef({ key: null, promise: null })

    // Membedakan request terbaru dari request yang sudah kedaluwarsa.
    const requestSequenceRef = useRef(0)

    const load = useCallback(async (requestedPage = 1, requestedSize = PAGE_SIZE) => {
        const page = Math.max(1, Number(requestedPage) || 1)
        const pageSize = Math.max(1, Number(requestedSize) || PAGE_SIZE)
        const key = `${page}:${pageSize}`

        // Jika request untuk halaman dan ukuran yang sama masih
        // berjalan, gunakan promise yang sama.
        if (requestRef.current.key === key && requestRef.current.promise) {
            return requestRef.current.promise
        }
        const requestSequence = ++requestSequenceRef.current

        const request = (async () => {
            setLoading(true)
            setError('')
            try {
                // Pagination DataTable dimulai dari 1,
                // sedangkan parameter API dimulai dari 0.
                const res = await notificationService.list(false, page - 1, pageSize)
                const data = res?.data || {}
                const content = Array.isArray(data) ? data : data.content || []
                const total = Array.isArray(data) ? content.length : Number(data.totalElements ?? data.totalData ?? content.length)

                // Jangan mengubah array response asli.
                const sortedContent = [...content].sort((a, b) => {
                    const dateA = new Date(a.createdAt || a.createdDate || 0).getTime()
                    const dateB = new Date(b.createdAt || b.createdDate || 0).getTime()
                    return dateB - dateA
                })

                // Abaikan response request lama jika pengguna
                // sudah berpindah halaman.
                if (requestSequence === requestSequenceRef.current) {
                    setItems(sortedContent)
                    setTotalRows(total)
                }
            } catch (e) {
                if (requestSequence === requestSequenceRef.current) {
                    console.error('Gagal memuat notifikasi:', e)
                    setError('Notifikasi tidak dapat dimuat.')
                }
            } finally {
                if (requestSequence === requestSequenceRef.current) {
                    setLoading(false)
                }
            }
        })()

        requestRef.current = { key, promise: request }
        try {
            await request
        } finally {
            // Jangan menghapus request lain yang mungkin
            // sudah menggantikan request ini.
            if (requestRef.current.promise === request) {
                requestRef.current = { key: null, promise: null }
            }
        }
    }, [notificationService])

    // Muat data ketika halaman pertama kali dibuka.
    useEffect(() => {
        load(1, PAGE_SIZE)
        return () => {
            // Membuat response yang masih berjalan menjadi kedaluwarsa.
            requestSequenceRef.current += 1
            requestRef.current = { key: null, promise: null }
        }
    }, [load])

    const open = useCallback(async item => {
        if (!item) return
        // Tandai sebagai dibaca.
        if (!item.read) {
            try {
                await notificationService.markRead(item.id)
                setItems(current =>
                    current.map(row => row.id === item.id ? { ...row, read: true } : row)
                )
            } catch (e) {
                console.error('Gagal menandai notifikasi sebagai dibaca:', e)
            }
        }

        if (!item.referenceId) {
            return
        }

        // Persetujuan dokumen.
        if (isApprovalNotification(item)) {
            navigate(`/smkhp/approval/${encodeURIComponent(item.referenceId)}`,
                {
                    state: { notification: item }
                }
            )
            return
        }

        // Dokumen yang sudah ditandatangani.
        if (isSignedDocumentNotification(item)) {
            navigate(`/signature/${item.referenceId}/signed`,
                {
                    state: { notification: item }
                }
            )
            return
        }

        // Pembuatan atau pembaruan sertifikat.
        if (isCertificateNotification(item)) {
            try {
                const certRes = await new RegistrasiService().checkSignatureCertificate()
                const cert = certRes?.data || {}
                const status = String(cert.statusCert ?? cert.status ?? '').toUpperCase()

                const expired = cert.expiredDate && new Date(cert.expiredDate).getTime() <= Date.now()
                const ready = Boolean(cert.id) &&
                    !expired &&
                    ['1', 'ACTIVE', 'VALID', 'AKTIF'].includes(status)

                if (ready) {
                    navigate(`/signature/${item.referenceId}`,
                        {
                            state: { notification: item }
                        }
                    )
                    return
                }
            } catch (e) {
                console.error('Gagal memeriksa sertifikat:', e)
            }

            navigate('/signature/certificate', {
                state: { notification: item }
            })
            return
        }

        // Proses tanda tangan dokumen.
        if (isSignatureNotification(item)) {
            try {
                const statusRes = await signatureService.getStatus(item.referenceId)

                if (statusRes?.data?.signed || statusRes?.data?.status === 'SIGNED') {
                    navigate(`/signature/${item.referenceId}/signed`,
                        {
                            state: { notification: item }
                        }
                    )
                    return
                }
            } catch (e) {
                console.error('Gagal memeriksa status tanda tangan:', e
                )
            }

            navigate(`/signature/${item.referenceId}`, {
                state: { notification: item }
            })
        }
    }, [navigate, notificationService])

    const handleChangePage = useCallback(page => {
        load(page, size)
    }, [load, size])

    const handleChangeRowsPerPage = useCallback((newSize, page) => {
        setSize(newSize)
        load(page, newSize)
    }, [load])

    // Kolom stabil agar DataTable tidak menerima array columns
    // baru pada setiap render.
    const columns = useMemo(
        () => [
            {
                name: 'Waktu',
                selector: row => row.createdAt || row.createdDate || '',
                sortable: true,
                width: '175px',
                cell: row => (
                    <span className="text-nowrap">
                        {formatDate(row.createdAt || row.createdDate)}
                    </span>
                )
            },
            {
                name: 'Notifikasi',
                grow: 2,
                cell: row => (
                    <div className="py-2">
                        <div className="d-flex align-items-center gap-2">
                            <strong>
                                {row.title || 'Notifikasi'}
                            </strong>

                            {!row.read && (
                                <Badge bg="primary">
                                    Baru
                                </Badge>
                            )}
                        </div>
                        <div className="small text-muted mt-1">
                            {row.message || '-'}
                        </div>
                    </div>
                )
            },
            {
                name: 'Jenis',
                width: '170px',
                cell: row =>
                    isApprovalNotification(row) ? (
                        <Badge bg="warning" text="dark">
                            Persetujuan
                        </Badge>
                    ) : isCertificateNotification(row) ? (
                        <Badge bg="warning" text="dark">
                            <FaKey className="me-1" />
                            Certificate
                        </Badge>
                    ) : isSignatureNotification(row) ? (
                        <Badge bg="primary">
                            <FaFileSignature className="me-1" />
                            Tanda tangan
                        </Badge>
                    ) : isSignedDocumentNotification(row) ? (
                        <Badge bg="success">
                            <FaFileSignature className="me-1" />
                            Dokumen TTE
                        </Badge>
                    ) : (
                        <Badge bg="secondary">
                            Informasi
                        </Badge>
                    )
            },
            {
                name: 'Status',
                width: '105px',
                cell: row =>
                    row.read ? (
                        <span className="text-muted">
                            <FaCheck className="me-1" />
                            Dibaca
                        </span>
                    ) : (
                        <Badge bg="danger">
                            Belum dibaca
                        </Badge>
                    )
            },
            {
                name: 'Aksi',
                width: '185px',
                cell: row =>
                    isActionable(row) ? (
                        <Button
                            size="sm"
                            variant={
                                isApprovalNotification(row)
                                    ? 'warning'
                                    : isCertificateNotification(row)
                                        ? 'warning'
                                        : isSignedDocumentNotification(row)
                                            ? 'success'
                                            : 'primary'
                            }
                            onClick={() => open(row)}
                        >
                            {isApprovalNotification(row)
                                ? 'Periksa & Setujui'
                                : isCertificateNotification(row)
                                    ? 'Buat / Perbarui Certificate'
                                    : isSignedDocumentNotification(row)
                                        ? 'Lihat Dokumen'
                                        : 'Tanda Tangani'}
                        </Button>
                    ) : null
            }
        ], [open])

    return (
        <div className="container-fluid py-4">
            <Card className="mb-4 bg-transparent border-0 shadow-none">
                <Card.Body className="px-1 py-0">
                    <Row className="align-items-end">
                        <Col md={7}>
                            <h3 className="text-white fw-bold mb-2">
                                Notifikasi
                            </h3>

                            <div className="text-white">
                                Informasi dan dokumen yang membutuhkan
                                tindakan Anda.
                            </div>
                        </Col>

                        <Col
                            md={5}
                            className="d-flex justify-content-md-end mt-3 mt-md-0"
                        >
                            <Button
                                variant="warning"
                                size="sm"
                                disabled={loading}
                                onClick={() => load(1, size)}
                            >
                                <FaArrowsRotate className="me-1" />
                                Refresh
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    {error && (
                        <div className="alert alert-danger m-3">
                            {error}
                        </div>
                    )}

                    <DataTable
                        columns={columns}
                        data={items}
                        responsive
                        highlightOnHover
                        noDataComponent={
                            <div className="py-5 text-muted">
                                Tidak ada notifikasi.
                            </div>
                        }
                        progressPending={loading}
                        progressComponent={
                            <div className="text-center py-5">
                                <Spinner animation="border" />
                                <div className="small text-muted mt-2">
                                    Memuat notifikasi...
                                </div>
                            </div>
                        }
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        paginationDefaultPage={1}
                        paginationRowsPerPageOptions={[10, 20, 50]}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                        persistTableHead
                    />
                </Card.Body>
            </Card>
        </div>
    )
}
